import type {
  BackstageUserIdentity,
  DiscoveryApi,
  IdentityApi,
  ProfileInfo,
} from '@backstage/core-plugin-api';

/* Remembers that the last sign-in was as guest, so a reload or deep link
 * restores the session instead of dropping back to the sign-in page. */
export const GUEST_SIGN_IN_STORAGE_KEY = 'vivicta:guestSignIn';

/* What the backend guest provider issues unless `auth.providers.guest.userEntityRef`
 * overrides it. */
export const GUEST_USER_ENTITY_REF = 'user:development/guest';

const GUEST_PROFILE: ProfileInfo = {
  displayName: 'Guest',
  email: 'guest@example.com',
};
const DEFAULT_TOKEN_LIFETIME_MS = 5 * 60 * 1000;
const TOKEN_EXPIRY_MARGIN_MS = 60 * 1000;

type GuestSession = {
  token: string;
  identity: BackstageUserIdentity;
  expiresAt: number;
};

const tokenExpiresAt = (token: string) => {
  try {
    const payload = token
      .split('.')[1]
      .replaceAll('-', '+')
      .replaceAll('_', '/');
    const { exp } = JSON.parse(window.atob(payload));
    if (typeof exp === 'number') {
      return exp * 1000 - TOKEN_EXPIRY_MARGIN_MS;
    }
  } catch {
    /* unreadable token — fall back to the default lifetime */
  }
  return Date.now() + DEFAULT_TOKEN_LIFETIME_MS;
};

export const rememberGuestSignIn = (remember: boolean) => {
  try {
    if (remember) {
      window.localStorage.setItem(GUEST_SIGN_IN_STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(GUEST_SIGN_IN_STORAGE_KEY);
    }
  } catch {
    /* storage unavailable (private mode) — the session just won't survive a reload */
  }
};

export const wasGuestSignIn = () => {
  try {
    return window.localStorage.getItem(GUEST_SIGN_IN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/** Whether the session is the dev guest rather than a real identity provider. */
export const isGuestSession = (userEntityRef?: string) =>
  userEntityRef === GUEST_USER_ENTITY_REF || wasGuestSignIn();

/**
 * Identity backed by the backend guest auth provider (`auth.providers.guest`).
 * Unlike a hand-built identity it carries a real Backstage token, so backend
 * plugins accept its requests.
 */
export class GuestIdentity implements IdentityApi {
  private session?: GuestSession;
  private pending?: Promise<GuestSession>;

  private constructor(private readonly discoveryApi: DiscoveryApi) {}

  /** Fetches a first token, so a misconfigured backend fails at sign-in. */
  static async signIn(discoveryApi: DiscoveryApi) {
    const identity = new GuestIdentity(discoveryApi);
    await identity.getSession();
    return identity;
  }

  async getProfileInfo() {
    return GUEST_PROFILE;
  }

  async getBackstageIdentity() {
    return (await this.getSession()).identity;
  }

  async getCredentials() {
    return { token: (await this.getSession()).token };
  }

  async signOut() {
    rememberGuestSignIn(false);
    this.session = undefined;
  }

  private getSession(): Promise<GuestSession> {
    if (this.session && Date.now() < this.session.expiresAt) {
      return Promise.resolve(this.session);
    }
    this.pending ??= this.fetchSession().finally(() => {
      this.pending = undefined;
    });
    return this.pending;
  }

  private async fetchSession() {
    const authBaseUrl = await this.discoveryApi.getBaseUrl('auth');
    const response = await fetch(`${authBaseUrl}/guest/refresh`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(
        `Guest sign-in failed with ${response.status} ${response.statusText}. Is auth.providers.guest enabled in app-config.local.yaml?`,
      );
    }
    const { backstageIdentity } = await response.json();
    this.session = {
      token: backstageIdentity.token,
      identity: backstageIdentity.identity,
      expiresAt: tokenExpiresAt(backstageIdentity.token),
    };
    return this.session;
  }
}
